import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Grid,
    Chip,
    Divider,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Close as CloseIcon,
    Replay as ReplayIcon,
    Download as DownloadIcon,
    Brush as BrushIcon,
    Category as CategoryIcon,
    SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { SCUIButton } from '@/atomic/atoms';
import { useTranslation } from 'react-i18next';
import AIProcessLogQuery, { AIProcessLogItem } from '../../../../services/query/AIProcessLogQuery';
import AIGeneratorCommandService from '../../../../services/studio/StudioCommand/AIGenerator/AIGeneratorCommand.services';
import { parseImageUrls, formatDate, getModelTypeLabel } from './AIGeneration.helper';

interface HistoryDetailModalProps {
    open: boolean;
    onClose: () => void;
    historyItem: AIProcessLogItem | null;
    onRegenerate: (item: AIProcessLogItem) => void;
}

const HistoryDetailModal: React.FC<HistoryDetailModalProps> = ({ open, onClose, historyItem, onRegenerate }) => {
    const { t } = useTranslation();
    const [detailData, setDetailData] = useState<AIProcessLogItem | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open && historyItem) {
            fetchDetailData(historyItem.id);
        }
    }, [open, historyItem]);

    const fetchDetailData = async (id: number) => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await AIProcessLogQuery.GetHistoryDetail(id);
            setDetailData(data);
        } catch (err: any) {
            console.error('Error fetching detail:', err);
            setError(err.message || 'Failed to load details');
        } finally {
            setIsLoading(false);
        }
    };

    if (!historyItem) return null;

    const sourcePhotos = detailData ? parseImageUrls(detailData.imageUrls || '[]') : [];
    const outputPhotos = detailData?.outputUrl ? parseImageUrls(detailData.outputUrl) : [];
    const firstOutputUrl = outputPhotos.length > 0 ? outputPhotos[0] : detailData?.outputUrl || '';

    const handleRegenerate = () => {
        if (detailData) {
            onRegenerate(detailData);
            onClose();
        }
    };

    const handleDownload = async () => {
        if (!detailData || !firstOutputUrl) return;

        const url = firstOutputUrl;
        const barcode = detailData.productBarcode || 'unknown';
        const urlExtension = detailData.outputFormat || url.split('.').pop()?.split('?')[0] || 'jpg';
        const filename = `${barcode}.${urlExtension}`;

        try {
            // Track the download in backend if predictionId is available
            const predictionId = detailData?.predictionId || historyItem.predictionId;

            // Eğer HTTP linki ise BFF proxy'si üzerinden indir ve track işlemini BFF halletsin
            if (url.startsWith('http://') || url.startsWith('https://')) {
                await AIGeneratorCommandService.DownloadImage(url, filename, predictionId);

                // Ayrıca BFF'nin MarkAsDownloaded'ına istek at
                // if (predictionId) {
                //     AIGeneratorCommandService.MarkAsDownloaded(predictionId).catch(console.error);
                // }
                return;
            }

            // Data URL veya lokal blob ise doğrudan tarayıcıda indir
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Download failed: ${response.statusText}`);
            }

            // Blob olarak al
            const blob = await response.blob();

            // Blob URL oluştur
            const blobUrl = window.URL.createObjectURL(blob);

            // Link oluşturup tıkla
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            // Temizlik
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);

            // Başarılı indirme sonrası tracking'i çağır
            // if (predictionId) {
            //     AIGeneratorCommandService.MarkAsDownloaded(predictionId).catch(console.error);
            // }
        } catch (error) {
            console.error('Download error:', error);
            // Fallback: Doğrudan linki indirmeyi dene
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        minHeight: '70vh',
                    },
                },
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                borderBottom="1px solid #e0e0e0"
            >
                <Typography variant="h5" fontWeight="600">
                    {t('mannequin.aiGeneration.hangerFlow.history.viewDetails')}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ p: 3 }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Grid container spacing={3}>
                        {/* Left Panel - Generated Photo */}
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Box>
                                <Typography variant="subtitle1" fontWeight="600" mb={1}>
                                    {t('mannequin.aiGeneration.hangerFlow.history.generatedPhoto')}
                                </Typography>
                                {firstOutputUrl ? (
                                    <Box
                                        component="img"
                                        src={firstOutputUrl}
                                        alt="Generated"
                                        sx={{
                                            width: '100%',
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 300,
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#f5f5f5',
                                        }}
                                    >
                                        <Typography color="textSecondary">
                                            {t('mannequin.aiGeneration.hangerFlow.history.noImage')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* Right Panel - Details */}
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            {/* Source Photos */}
                            <Box mb={3}>
                                <Typography variant="subtitle1" fontWeight="600" mb={1}>
                                    {t('mannequin.aiGeneration.hangerFlow.history.sourcePhotos')} ({sourcePhotos.length}
                                    )
                                </Typography>
                                <Grid container spacing={1}>
                                    {sourcePhotos.map((url, index) => (
                                        <Grid
                                            key={index}
                                            size={{
                                                xs: 4,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={url}
                                                alt={`Source ${index + 1}`}
                                                sx={{
                                                    width: '100%',
                                                    height: 100,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    border: '1px solid #e0e0e0',
                                                }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Prompt */}
                            <Box mb={2}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    {t('mannequin.aiGeneration.hangerFlow.history.prompt')}
                                </Typography>
                                <Typography variant="body2">
                                    {detailData?.prompt || historyItem.prompt || '-'}
                                </Typography>
                            </Box>

                            {/* Barcode */}
                            <Box mb={2}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    {t('mannequin.aiGeneration.hangerFlow.history.barcode')}
                                </Typography>
                                <Chip
                                    label={detailData?.productBarcode || historyItem.productBarcode || 'No Barcode'}
                                    color="primary"
                                    variant="outlined"
                                />
                                {(detailData?.isInpainting || historyItem.isInpainting) && (
                                    <Chip
                                        icon={<BrushIcon />}
                                        label={t('mannequin.aiGeneration.hangerFlow.history.inpainting')}
                                        color="secondary"
                                        variant="filled"
                                        size="small"
                                    />
                                )}
                                {(detailData?.modelType !== undefined || historyItem.modelType !== undefined) && (
                                    <Chip
                                        icon={<SmartToyIcon />}
                                        label={`${t('mannequin.aiGeneration.hangerFlow.history.model')}: ${getModelTypeLabel(detailData?.modelType ?? historyItem.modelType)}`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            </Box>

                            {/* Generation Type & Inpainting Info */}
                            <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                                {(detailData?.generationTypeName ||
                                    detailData?.generationTypeCode ||
                                    historyItem.generationTypeName ||
                                    historyItem.generationTypeCode) && (
                                    <Chip
                                        icon={<CategoryIcon />}
                                        label={`${t('mannequin.aiGeneration.hangerFlow.history.generationType')}: ${detailData?.generationTypeName || detailData?.generationTypeCode || historyItem.generationTypeName || historyItem.generationTypeCode}`}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {(detailData?.isInpainting || historyItem.isInpainting) && (
                                    <Chip
                                        icon={<BrushIcon />}
                                        label={t('mannequin.aiGeneration.hangerFlow.history.inpainting')}
                                        color="secondary"
                                        variant="filled"
                                        size="small"
                                    />
                                )}
                                {(detailData?.modelType !== undefined || historyItem.modelType !== undefined) && (
                                    <Chip
                                        icon={<SmartToyIcon />}
                                        label={`${t('mannequin.aiGeneration.hangerFlow.history.model')}: ${getModelTypeLabel(detailData?.modelType ?? historyItem.modelType)}`}
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Configuration */}
                            <Box mb={2}>
                                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                    {t('mannequin.aiGeneration.hangerFlow.history.configuration')}
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid
                                        size={{
                                            xs: 6,
                                        }}
                                    >
                                        <Typography variant="body2" color="textSecondary">
                                            {t('mannequin.aiGeneration.configuration.aspectRatio')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {detailData?.aspectRatio || historyItem.aspectRatio}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 6,
                                        }}
                                    >
                                        <Typography variant="body2" color="textSecondary">
                                            {t('mannequin.aiGeneration.configuration.resolution')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {detailData?.resolution || historyItem.resolution}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 6,
                                        }}
                                    >
                                        <Typography variant="body2" color="textSecondary">
                                            {t('mannequin.aiGeneration.configuration.outputFormat')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {detailData?.outputFormat || historyItem.outputFormat}
                                        </Typography>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 6,
                                        }}
                                    >
                                        <Typography variant="body2" color="textSecondary">
                                            {t('mannequin.aiGeneration.hangerFlow.history.status')}
                                        </Typography>
                                        <Chip
                                            label={detailData?.status || historyItem.status}
                                            size="small"
                                            color={
                                                (detailData?.status || historyItem.status) === 'success'
                                                    ? 'success'
                                                    : 'default'
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Generation Info */}
                            <Box>
                                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                    {t('mannequin.aiGeneration.hangerFlow.history.generationDate')}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {formatDate(detailData?.createdDate || historyItem.createdDate)}
                                </Typography>
                                {(detailData?.predictionTimeSeconds || historyItem.predictionTimeSeconds) && (
                                    <Typography variant="body2" color="textSecondary">
                                        {t('mannequin.aiGeneration.hangerFlow.history.duration')}:{' '}
                                        {(
                                            detailData?.predictionTimeSeconds || historyItem.predictionTimeSeconds
                                        )?.toFixed(2)}{' '}
                                        {t('mannequin.aiGeneration.hangerFlow.history.seconds')}
                                    </Typography>
                                )}
                                {(detailData?.cost || historyItem.cost) && (
                                    <Typography variant="body2" color="textSecondary">
                                        {t('mannequin.aiGeneration.hangerFlow.history.cost')}: $
                                        {(detailData?.cost || historyItem.cost)?.toFixed(4)}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', gap: 2 }}>
                <SCUIButton
                    text={t('mannequin.aiGeneration.hangerFlow.history.close')}
                    size="m"
                    sizeType="default"
                    type="secondary"
                    onClick={onClose}
                />
                <SCUIButton
                    text={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.download')}
                    size="m"
                    sizeType="default"
                    type="primary"
                    onClick={handleDownload}
                    disabled={!detailData || !firstOutputUrl || isLoading}
                    showStartIcon
                    startIcon={<DownloadIcon />}
                />
                {!(detailData?.isInpainting || historyItem.isInpainting) && (
                    <SCUIButton
                        text={t('mannequin.aiGeneration.hangerFlow.history.reuseButton')}
                        size="m"
                        sizeType="default"
                        type="primary"
                        onClick={handleRegenerate}
                        disabled={!detailData || isLoading}
                        showStartIcon
                        startIcon={<ReplayIcon />}
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};

export default HistoryDetailModal;
